import type { PricesUpdatedPayload, IntradayPriceItem } from "socket/intraday-prices"
import type {
  IntradayStandalonePerformanceItem,
  StandalonePerformanceByPeriod,
  StandalonePeriod,
} from "socket/intraday-standalone-performance"

interface IntradayGathererDescriptorBase {
  type: "intraday"
  key: [string, string, string]
  modelId: string
  prefix: string
}

export interface IntradayPricesGathererDescriptor extends IntradayGathererDescriptorBase {
  streamType: "prices"
}

export interface IntradayStandaloneGathererDescriptor extends IntradayGathererDescriptorBase {
  streamType: "standalone-performance"
  period: StandalonePeriod
}

export type IntradayGathererDescriptor =
  | IntradayPricesGathererDescriptor
  | IntradayStandaloneGathererDescriptor

// Normalize the key to match the contributor tree key format produced by
// buildContributorKey, which replaces dots with underscores.
function normalizeContributorKey(rowId: string): string {
  return rowId.replace(/\./g, "_")
}

export function buildIntradayFlatMap(
  intradayPrices: PricesUpdatedPayload,
): Record<string, IntradayPriceItem> {
  if (!intradayPrices) {
    return {}
  }
  const result: Record<string, IntradayPriceItem> = {}
  for (const rowId of Object.keys(intradayPrices)) {
    const info = intradayPrices[rowId]
    if (info == null) continue
    result[normalizeContributorKey(rowId)] = info
  }
  return result
}

export function createIntradayContributorsQuery(params: {
  modelId: string
  prefix: string
}): IntradayPricesGathererDescriptor {
  const { modelId, prefix } = params
  return {
    type: "intraday",
    key: ["intraday", modelId, prefix],
    modelId,
    prefix,
    streamType: "prices",
  }
}

export function buildIntradayStandaloneFlatMap(
  standalonePerformanceByPeriod: StandalonePerformanceByPeriod,
  period: StandalonePeriod,
): Record<string, IntradayStandalonePerformanceItem> {
  if (!standalonePerformanceByPeriod) {
    return {}
  }

  const periodRows = standalonePerformanceByPeriod[String(period)]
  if (!periodRows) {
    return {}
  }

  return Object.keys(periodRows).reduce<Record<string, IntradayStandalonePerformanceItem>>((result, rowId) => {
    const info = periodRows[rowId]
    if (info != null) {
      result[normalizeContributorKey(rowId)] = info
    }
    return result
  }, {})
}

export function createIntradayStandaloneContributorsQuery(params: {
  modelId: string
  prefix: string
  period: StandalonePeriod
}): IntradayStandaloneGathererDescriptor {
  const { modelId, prefix, period } = params
  return {
    type: "intraday",
    key: ["intraday-standalone", modelId, prefix],
    modelId,
    prefix,
    streamType: "standalone-performance",
    period,
  }
}
